<script setup lang="ts">
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import EditDatesTimeslot from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/EditDatesTimeslot.vue";
import { useAddSessionFlow } from "~/composables/trainings/useAddSessionFlow";
import CardDatesTimeslot from "./CardDatesTimeslot.vue";

defineProps<{
  /** Set when the admin tried to create the session without a single validated step. */
  missingTimeslot: boolean;
}>();

const emit = defineEmits<{
  /** Open a fresh step form, guarded against dropping a step already being edited. */
  addStep: [];
  /** Blank out the step form in place — a session always needs at least one step. */
  resetStep: [];
}>();

const flow = useAddSessionFlow();

/** Hours already booked by the other steps, so the form can cap the one being edited. */
const timeslotsDuration = computed(() =>
  (flow.sessions.sessionDraft?.sessionTimeslot ?? []).reduce(
    (sum, timeslot) => sum + timeslot.trainingDurationHours,
    0,
  ),
);

/** "Ajouter" on the step form: the step only joins the session once it is validated. */
function saveTimeslot() {
  const draft = flow.sessions.sessionDraft;
  if (draft?.currentSessionTimeslot) {
    draft.sessionTimeslot?.push(draft.currentSessionTimeslot);
    draft.currentSessionTimeslot = null;
  }
  flow.sessions.showAddTimeslot = false;
}

/** "Annuler" on the step form: closing it is only allowed once another step is saved. */
function onCancelTimeslot() {
  if (flow.sessions.sessionDraft?.sessionTimeslot?.length) {
    flow.sessions.showAddTimeslot = false;
  } else {
    emit("resetStep");
  }
}
</script>

<template>
  <div v-if="flow.sessions.sessionDraft" class="flex flex-col">
    <h4 class="heading_4_demi">Étapes de la session</h4>

    <span
      v-if="missingTimeslot && !flow.sessions.sessionDraft.sessionTimeslot?.length"
      class="mt-2 text-primary-red-color body_regular"
    >
      Veuillez valider l'étape pour ajouter la session
    </span>

    <div class="mt-4 flex flex-col gap-3">
      <div v-for="(timeslot, index) in flow.sessions.sessionDraft.sessionTimeslot" :key="index">
        <CardDatesTimeslot :timeslot="timeslot" :index="index" />
      </div>
      <EditDatesTimeslot
        v-if="flow.sessions.showAddTimeslot && flow.sessions.sessionDraft.currentSessionTimeslot"
        :error="missingTimeslot"
        :async-action="saveTimeslot"
        :tmp-session-timeslots="flow.sessions.sessionDraft.currentSessionTimeslot"
        :create="true"
        :trainingDuration="flow.training.durationHours"
        :timeslotsDuration="timeslotsDuration"
        @close="onCancelTimeslot"
      />
    </div>

    <AppButton type="link" icon="add" label="Ajouter une étape" class="mt-4 mx-auto" @click="emit('addStep')" />
  </div>
</template>
